/* PlannerPerToothCard — Planner Submission-tab read-only wrap for the
 * doctor-authored (or planner-authored) per-tooth plan.
 *
 * Accordion HEADER matches the other planner Submission-tab sections
 * (PlannerCaseBriefCard's Section: uppercase tracked title, 3px
 * primary-green left bar, light-secondary header bar). Accordion
 * BODY reuses the exact same per-tooth UI as the doctor's Treatment
 * tab (PerToothPlanView from PlanApprovalHero) — 4 tabs (Movements /
 * Attachments / Precision cuts / Bite ramps) with FDI ↔ Patient-view
 * toggle, linear 2-row chart, and Authored Summary list.
 *
 * Self-rendering — returns null when there's no per-tooth data, so
 * CoE-only cases without doctor authoring don't render an empty card.
 */
import React, { useEffect, useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { PerToothPlanView } from "@/components/case/PlanApprovalHero";


function pickPrimarySource(plan, prescription) {
    const hasPlanData = !!(plan && (
        (plan.tooth_movements_plan || []).length
        || (plan.attachments_plan || []).length
        || (plan.precision_cuts || []).length
        || plan.bite_ramps?.enabled
    ));
    return hasPlanData ? plan : (prescription || null);
}


export default function PlannerPerToothCard({ c, number }) {
    const source = pickPrimarySource(c?.plan, c?.prescription);
    const hasAnything = !!(source && (
        (source.tooth_movements_plan || []).length
        || (source.attachments_plan || []).length
        || (source.precision_cuts || []).length
        || source.bite_ramps?.enabled
    ));

    /* Persisted per-case so the planner's preference doesn't reset on
       navigation, matching PlannerCaseBriefCard's Section behaviour.
       Defaults COLLAPSED — user has repeatedly asked for every accordion
       to start closed so the page isn't a wall of expanded sections.
       iter98.99do — bumped storage key prefix to invalidate stale `=1`
       entries from sessions before the collapsed-by-default fix shipped. */
    const storageKey = `plannerBrief2:${c?.id || "default"}:per-tooth-plan`;
    const [open, setOpen] = useState(() => {
        try {
            const v = localStorage.getItem(storageKey);
            return v === null ? false : v === "1";
        } catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch { /* noop */ }
    }, [open, storageKey]);

    if (!hasAnything) return null;

    return (
        <div
            className="bg-card rounded-2xl border border-border/70 overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
            data-testid="planner-section-per-tooth-plan"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full px-5 py-3 border-b border-border/60 flex items-center gap-3 hover:bg-teal-50/40 transition-colors text-left group"
                style={{ background: "linear-gradient(to right, rgba(20,184,166,0.05) 0%, transparent 55%)" }}
                aria-expanded={open}
                data-testid="planner-section-per-tooth-plan-toggle"
            >
                {number != null && (
                    <span className="font-display text-[13px] font-bold tabular-nums shrink-0 w-6 text-teal-700">
                        {String(number).padStart(2, "0")}
                    </span>
                )}
                <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-teal-900/80 flex-1">
                    Per-Tooth Plan — Authored Movements
                </h3>
                <CaretDown
                    size={13}
                    weight="bold"
                    className={`text-teal-700 transition-transform group-hover:scale-110 ${open ? "" : "-rotate-90"}`}
                />
            </button>
            {open && (
                <div className="p-5" data-testid="planner-per-tooth-body">
                    <PerToothPlanView plan={source} />
                </div>
            )}
        </div>
    );
}
